import { Grid } from '@mui/material';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import { Header, Page } from 'redesign';
import { useAlert } from 'shared/components/alerts';
import { CompanyInput } from 'shared/components/shift-forms/components/forms/shared/inputs/company-input';
import { hqRoutes } from 'hq/Routes';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { stringifyFilters } from 'shared/utils/stringify-filters';
import { HQProjectFormTypeEdit } from 'shared/components/project-forms/types/project-form.types';
import { HQProjectPayloadEdit } from 'hq/services/project-planning-service.types';
import { DetailsForWorkTemplate } from 'shared/components/work-forms/details/work-form-details.types';
import { invariant } from 'shared/utils/utils';
import { Companies } from 'api/services/company/company-service.schema';
import { ProjectEditFormProvider } from './project-edit-form-provider';
import { useProjectPlanningPage } from '../../../table/hooks/use-project-planning';
import { useEditProject } from '../../../hooks/use-edit-project';

type ProjectCreateProps = {
  companies: Companies;
  defaultValues?: Partial<HQProjectFormTypeEdit>;
  successMessage: string;
  pageTitle: string;
  projectId: string;
  isProjectWithActivePlacement: boolean;
  workDetails: DetailsForWorkTemplate;
};

export const ProjectEditFormPage = ({
  companies,
  defaultValues,
  successMessage,
  pageTitle,
  projectId,
  isProjectWithActivePlacement,
  workDetails,
}: ProjectCreateProps) => {
  const { alertSuccess, alertError } = useAlert();
  const formMethods = useForm<HQProjectFormTypeEdit>({
    defaultValues,
  });
  const navigate = useNavigate();
  const generatePath = useHqRelativePath();
  const mutation = useEditProject();
  const { params } = useProjectPlanningPage();

  const { companyOption } = formMethods.watch();

  const handleSubmit = useCallback(() => {
    formMethods.handleSubmit((data: HQProjectFormTypeEdit) => {
      invariant(data.location, 'location is required');

      const payload: HQProjectPayloadEdit = {
        departmentId: data.departmentId,
        startDate: data.startDate,
        endDate: data.endDate,
        location: data.location,
        weekHourVolume: +data.weeklyHours,
        freelanceProperties: data.isFreelance
          ? {
              hourlyRateCents: Math.round(+data.hourlyRate * 100),
              ortApplicable: data.isFreelanceOrtApplicable ?? false,
            }
          : undefined,
        tempWorkProperties: data.isTempwork
          ? {
              ortApplicable: data.isTempworkOrtApplicable ?? false,
            }
          : undefined,
      };
      mutation.mutate(
        { projectId, projectData: payload },
        {
          onSuccess: () => {
            alertSuccess(successMessage);
            navigate(
              generatePath(
                hqRoutes.PlannerProject,
                {},
                { ...stringifyFilters(params), projectDetailId: projectId }
              )
            );
          },
          onError: (e) => alertError(e),
        }
      );
    })();
  }, [projectId]);

  const onCancel = useCallback(
    () => navigate(generatePath(hqRoutes.PlannerProject, {}, stringifyFilters(params))),
    [params, generatePath, navigate]
  );

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText={pageTitle} />}
        toolbox={
          <ToolboxForm
            onSubmit={handleSubmit}
            mutationStatus={mutation.status}
            mode="update"
            onCancel={onCancel}
          />
        }
      >
        <Grid container>
          <Grid item xs={12}>
            <CompanyInput companies={companies} disabled={true}></CompanyInput>
            {companyOption.value ? (
              <ProjectEditFormProvider
                isProjectWithActivePlacement={isProjectWithActivePlacement}
                workDetails={workDetails}
              />
            ) : null}
          </Grid>
        </Grid>
      </Page>
    </FormProvider>
  );
};
